import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { logout } from '../../redux/slices/authSlice';
import alertify from 'alertifyjs';
import PropTypes from 'prop-types';

/**
 * UserAvatarMenu - Reusable user avatar dropdown menu
 * 
 * Features:
 * - User avatar with first name initial
 * - Dropdown menu with user info
 * - Navigation to Dashboard, Profile
 * - Logout functionality
 * - Material-UI based (consistent with dashboard)
 * 
 * Props:
 * @param {boolean} showGreeting - Show "Merhaba, [Ad]" text (default: true)
 * @param {string} variant - 'navbar' | 'dashboard' (styling variants)
 * @param {function} onNavigate - Optional callback after navigation
 * 
 * @returns {JSX.Element} UserAvatarMenu component
 */
const UserAvatarMenu = ({ showGreeting = true, variant = 'dashboard', onNavigate }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleDashboard = () => {
    handleMenuClose();
    history.push('/dashboard');
    if (onNavigate) onNavigate();
  };

  const handleProfile = () => {
    handleMenuClose();
    history.push('/profile');
    if (onNavigate) onNavigate();
  };

  const handleLogout = () => {
    handleMenuClose();
    dispatch(logout());
    alertify.success('Çıkış yapıldı');
    history.push('/');
    if (onNavigate) onNavigate();
  };

  // Guard: No user
  if (!user) {
    return null;
  }

  // UserType label
  const getUserTypeLabel = () => {
    if (user.userType === 'company_owner') return '🏢 Şirket Sahibi';
    if (user.userType === 'company_member') return '👥 Şirket Üyesi';
    if (user.userType === 'system_admin') return '⚙️ Sistem Yöneticisi';
    return '👤 Bireysel Kullanıcı';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Greeting Text (optional) */}
      {showGreeting && (
        <Typography
          variant="body2"
          sx={{
            display: { xs: 'none', sm: 'block' },
            color: variant === 'navbar' ? 'text.primary' : 'text.primary'
          }}
        >
          Merhaba, {user.firstName}
        </Typography>
      )}

      {/* Avatar Button */}
      <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
          {user.firstName?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 240,
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {user.email}
          </Typography>
          <Chip
            label={getUserTypeLabel()}
            size="small"
            color="primary"
            sx={{ mt: 1, height: 20, fontSize: '0.7rem' }}
          />
        </Box>
        <Divider />

        {/* Menu Items */}
        <MenuItem onClick={handleDashboard}>
          <DashboardIcon sx={{ mr: 1.5 }} fontSize="small" />
          Dashboard
        </MenuItem>
        <MenuItem onClick={handleProfile}>
          <AccountCircleIcon sx={{ mr: 1.5 }} fontSize="small" />
          Profil
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <LogoutIcon sx={{ mr: 1.5 }} fontSize="small" />
          Çıkış Yap
        </MenuItem>
      </Menu>
    </Box>
  );
};

UserAvatarMenu.propTypes = {
  showGreeting: PropTypes.bool,
  variant: PropTypes.oneOf(['navbar', 'dashboard']),
  onNavigate: PropTypes.func
};

export default UserAvatarMenu;

